import { useConnectModal } from '@rainbow-me/rainbowkit'
import { Text } from 'components'
import { userAccountAtom } from 'context/account'
import type { CurrencyMetadata, ProductMetadata } from 'indices-list'
import { useAtom, useAtomValue } from 'jotai'
import React, { useEffect, useState } from 'react'
import { TransactionStatus } from 'sdk/omni/omni-transaction-service/types'
import { storedTransactionsAtom, useRefresh } from 'services'
import { wagmiConfig } from 'services/wallet/chains'
import { formatInputValue } from 'utils'
import { isAddressEqual, zeroAddress } from 'viem'
import { useAccount, useSwitchChain } from 'wagmi'
import { BuyComponent } from './buy'
import { BuySellTabComponent } from './buy-sell-tab'
import { SellComponent } from './sell'
import { createAtomicInvestStorage, transactionExecution } from './storage'
import type { InvestStorage } from './storage/interface'
import {
  InvestStateErrors,
  type InvestStateStatus,
  type SuccessPayload,
} from './storage/invest-states'
import {
  RebalanceTooltipBody,
  RebalanceTooltipContent,
  StyledRebalancingText,
  TooltipWrapper,
  Wrapper,
} from './styled'

type TransactionalProps = {
  productMetadata: ProductMetadata
}

interface SelectedModeProps {
  productMetadata: ProductMetadata
  store: InvestStorage
  inputAmount: string
  isButtonDisabled: boolean
  isTransactionInPending: boolean
  isRetry: boolean
  onInputChange: (amount: string) => void
  onAssetChange: (asset: CurrencyMetadata) => void
  onButtonClick: () => void
  isProcessing: boolean
}

export const SelectedModeComponent: React.FC<SelectedModeProps> = ({
  productMetadata,
  isButtonDisabled,
  isTransactionInPending,
  isRetry,
  store,
  inputAmount,
  isProcessing,
  onInputChange,
  onAssetChange,
  onButtonClick,
}) => {
  if (
    store.input.asset.address === productMetadata.address ||
    isRetry ||
    (isTransactionInPending && store.input.asset.type === 'index-v2')
  ) {
    // TODO:  get rid of 2(buy/sell), unify to one
    // TODO: task
    return (
      <SellComponent
        productMetadata={productMetadata}
        store={store}
        isButtonDisabled={isButtonDisabled}
        isRetry={isRetry}
        isTransactionInPending={isTransactionInPending}
        inputAmount={inputAmount}
        isProcessing={isProcessing}
        onInputChange={onInputChange}
        onAssetChange={onAssetChange}
        onButtonClick={onButtonClick}
      />
    )
  }
  return (
    <BuyComponent
      productMetadata={productMetadata}
      store={store}
      inputAmount={inputAmount}
      isProcessing={isProcessing}
      isButtonDisabled={isButtonDisabled}
      isRetry={isRetry}
      isTransactionInPending={isTransactionInPending}
      onInputChange={onInputChange}
      onAssetChange={onAssetChange}
      onButtonClick={onButtonClick}
    />
  )
}

export const InvestComponent: React.FC<TransactionalProps> = ({ productMetadata }) => {
  const store = createAtomicInvestStorage(productMetadata)
  // const { navigationEvent } = useAnalyticsEventTracker(AnalyticsArea.Widget)
  // const dexName = isAddressEqual(productMetadata.address, CTEST.address) ? 'Matcha' : 'Aerodrome'
  // const dexLink = isAddressEqual(productMetadata.address, CTEST.address)
  //   ? `https://matcha.xyz/tokens/base/${productMetadata.address}?buyChain=8453&buyAddress=${productMetadata.address}&sellChain=8453`
  //   : 'https://aerodrome.finance/swap?from=eth&to=0x83190636c344cf1220ffcbfc0c1198f7867b3667&chain0=8453&chain1=8453'
  // const dexIcon = isAddressEqual(productMetadata.address, CTEST.address)
  //   ? MatchaIconBlack
  //   : AerodromeIcon

  // if (isAddressEqual(productMetadata.address, CTEST.address)) {
  //   return (
  //     <TooltipWrapper>
  //       <Wrapper style={{ justifyContent: 'flex-end' }}>
  //         <CoverWrapper>
  //           <CoverIconsWrapper>
  //             <ProductIcon
  //               src={isAddressEqual(productMetadata.address, CTEST.address) ? COOKIE : JMXIcon}
  //             />
  //             <ProductIcon src={dexIcon} />
  //           </CoverIconsWrapper>
  //           <Text type="two" weight={Weight.Bold}>
  //             {`Start your ${productMetadata.symbol.toUpperCase()} journey`}
  //           </Text>
  //         </CoverWrapper>

  //         <StyledButton onClick={() => navigationEvent(dexLink, true)} buttonType="primary">
  //           {`Trade ${productMetadata.symbol.toUpperCase()} on ${dexName} DEX`}
  //         </StyledButton>
  //       </Wrapper>
  //     </TooltipWrapper>
  //   )
  // }

  const { dispatch, input, investState } = store
  const [transactionProcessing, setTransactionProcessing] = useAtom(transactionExecution)

  const transactions = useAtomValue(storedTransactionsAtom)
  const userAddress = useAtomValue(userAccountAtom)
  const usersTransactions = transactions.filter(({ account }) =>
    isAddressEqual(account, userAddress ?? zeroAddress),
  )

  const { fastRefresh } = useRefresh()
  const { openConnectModal } = useConnectModal()
  const { isConnected, isConnecting } = useAccount()
  const { switchChain } = useSwitchChain({ config: wagmiConfig })

  const [amount, setAmount] = React.useState<string>('')
  const [successDone, setSuccessDone] = useState<boolean | undefined>(false)
  const [pendingTransaction, setPendingTransaction] = React.useState<boolean>(false)

  const handleInput = (amount: string) => {
    const formattedAmount = formatInputValue(amount, input.asset.decimals)
    setAmount(formattedAmount)
    dispatch({
      type: 'input-change',
      payload: { amount: formattedAmount },
    })
  }

  const resetInvestState = () => {
    setAmount('')
    dispatch({
      type: 'input-change',
      payload: { amount: '' },
    })
  }

  const selectAsset = (asset: CurrencyMetadata) => {
    dispatch({
      type: 'asset-select',
      payload: { asset },
    })
  }

  const handleClick = () => {
    if (
      store.investState.payload === InvestStateErrors.Disconnected &&
      !isConnected &&
      !isConnecting &&
      openConnectModal
    ) {
      openConnectModal()
    } else if (
      store.investState.payload === InvestStateErrors.UnsupportedNetwork &&
      store.investState.chainToSwitch &&
      switchChain
    ) {
      return switchChain({ chainId: store.investState.chainToSwitch })
    } else {
      let payload: InvestStateStatus = { ...store.investState }
      if (
        store.investState.type === 'success' &&
        (store.investState.payload.inputCurrency.type === 'index-v2' ||
          store.investState.payload.outputCurrency.type === 'index-v2')
      ) {
        setTransactionProcessing(true)
        payload = {
          ...store.investState,
          payload: {
            ...store.investState.payload,
            stopProcessing: () =>
              setTimeout(() => {
                setTransactionProcessing(false)
              }, 1000),
            clearState: () => resetInvestState(),
          } as SuccessPayload,
        }
      }

      return store.dispatch({
        type: 'transaction-confirm',
        payload,
      })
    }
  }

  const isDisconnected =
    investState.type === 'error' && investState.payload === InvestStateErrors.Disconnected

  const isWrongNetwork =
    investState.type === 'error' && investState.payload === InvestStateErrors.UnsupportedNetwork

  const isRetry = investState.type === 'success' && investState.payload.isRetry

  const isRebalanceRunning =
    investState.type === 'error' && investState.payload === InvestStateErrors.RebalancePending

  const isStoreTransactionInPending =
    store.pendingTransactionStatus !== undefined &&
    store.pendingTransactionStatus === TransactionStatus.INFLIGHT

  const isButtonEnabled =
    (isDisconnected || isWrongNetwork || investState.type === 'success' || isRetry) &&
    !pendingTransaction &&
    !transactionProcessing

  useEffect(() => {
    const pending = isStoreTransactionInPending
      ? usersTransactions.some((tx) => tx.status === TransactionStatus.INFLIGHT)
      : usersTransactions.some((tx) => tx.status === TransactionStatus.INFLIGHT) ||
        Boolean(isStoreTransactionInPending)

    setTimeout(() => {
      setPendingTransaction(pending)
    }, 750)
  }, [fastRefresh, transactions, isStoreTransactionInPending])

  useEffect(() => {
    if (successDone) {
      handleInput('')
    }
  }, [successDone])

  useEffect(() => {
    store.dispatch({
      type: 'refresh-allowance',
    })
  }, [pendingTransaction])

  return (
    <TooltipWrapper>
      <Wrapper>
        <BuySellTabComponent
          isPending={pendingTransaction && input.asset.type === 'index-v2'}
          isRetryInPending={
            pendingTransaction &&
            input.asset.type === 'index-v2' &&
            usersTransactions[usersTransactions.length - 1] &&
            usersTransactions[usersTransactions.length - 1].action.action === 'retry'
          }
          isRetry={isRetry}
          productMetadata={productMetadata}
          store={store}
          setSuccessDone={setSuccessDone}
          successDone={successDone}
        />
        {!successDone && (
          <SelectedModeComponent
            productMetadata={productMetadata}
            store={store}
            inputAmount={amount}
            isButtonDisabled={!isButtonEnabled}
            isTransactionInPending={pendingTransaction}
            isRetry={isRetry}
            isProcessing={transactionProcessing}
            onInputChange={handleInput}
            onAssetChange={selectAsset}
            onButtonClick={handleClick}
          />
        )}
      </Wrapper>
      {isRebalanceRunning && (
        <RebalanceTooltipBody>
          <RebalanceTooltipContent>
            <Text type={'h5'}>!</Text>
            <StyledRebalancingText>
              {productMetadata.symbol.toUpperCase()} is being rebalanced and is temporarily
              unavailable. Please try again soon.
            </StyledRebalancingText>
          </RebalanceTooltipContent>
        </RebalanceTooltipBody>
      )}
    </TooltipWrapper>
  )
}
